import { Track } from '@spotify/web-api-ts-sdk';
import { CSSProperties, MouseEvent, ReactNode, useEffect, useState } from 'react';
import MusicDetailBottomSheet from '@components/music/music-detail-bottom-sheet/MusicDetailBottomSheet';
import { Layout, SvgIcon, Typo } from '@design-system';
import SpotifyManager from '@libs/SpotifyManager';
import { User } from '@models/user';
import { FontType } from 'src/design-system/Font/Font.types';
import { SkeletonBox, StyledSpotifyMusic } from './SpotifyMusic.styled';

interface Props {
  /* track id 또는 track data 전달. */
  track?: string | Track | null;
  sharer?: User;
  useDetailBottomSheet?: boolean;
  useAlbumImg?: boolean;
  containerStyle?: CSSProperties;
  fontType?: FontType;
  onClick?: () => void;
  /** Optional content rendered inside the chip after the title (e.g. an
   *  inline [UP] pill on the friends list). */
  rightSlot?: ReactNode;
}

function SpotifyMusic({
  track,
  sharer,
  containerStyle,
  fontType = 'label-medium',
  useDetailBottomSheet = false,
  useAlbumImg = false,
  onClick,
  rightSlot,
}: Props) {
  const [trackData, setTrackData] = useState<Track | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const spotifyManager = SpotifyManager.getInstance();

  const [showMusicDetail, setShowMusicDetail] = useState(false);

  const handleClickMusic = (e: MouseEvent) => {
    e.stopPropagation();

    if (!useDetailBottomSheet) {
      onClick?.();
      return;
    }
    setShowMusicDetail(true);
  };

  useEffect(() => {
    if (!track) {
      setTrackData(null);
      setIsLoading(false);
      return;
    }
    if (typeof track !== 'string') {
      setTrackData(track);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    spotifyManager
      .getTrack(track)
      .then((data) => {
        setTrackData(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [spotifyManager, track]);

  return (
    <>
      <StyledSpotifyMusic type="button" onClick={handleClickMusic} disabled={false}>
        {isLoading ? (
          <Layout.FlexRow
            w="100%"
            outline="SPOTIFY_GREEN"
            rounded={8}
            justifyContent="center"
            alignItems="center"
            pv={4}
            ph={8}
            gap={4}
            bgColor="WHITE"
            style={{
              ...containerStyle,
              minHeight: 28,
            }}
          >
            <SpotifyIcon />
            <SkeletonBox />
          </Layout.FlexRow>
        ) : (
          !!trackData && (
            <Layout.FlexRow
              w="100%"
              outline="SPOTIFY_GREEN"
              rounded={8}
              justifyContent="center"
              alignItems="center"
              pv={4}
              ph={8}
              gap={4}
              bgColor="WHITE"
              style={{
                ...containerStyle,
              }}
            >
              {useAlbumImg ? (
                <img
                  src={trackData.album.images[0].url}
                  width={16}
                  height={16}
                  alt={`${trackData.name}-album`}
                  style={{
                    borderRadius: 4,
                  }}
                />
              ) : (
                <SpotifyIcon />
              )}
              <Typo type={fontType} numberOfLines={1}>
                {trackData.artists[0]?.name
                  ? `${trackData.artists[0].name} - ${trackData.name}`
                  : trackData.name}
              </Typo>
              {useAlbumImg && <SpotifyIcon />}
              {rightSlot}
            </Layout.FlexRow>
          )
        )}
      </StyledSpotifyMusic>
      {useDetailBottomSheet && (
        <MusicDetailBottomSheet
          visible={showMusicDetail}
          closeBottomSheet={() => {
            setShowMusicDetail(false);
          }}
          sharer={sharer}
          track={trackData}
        />
      )}
    </>
  );
}
export default SpotifyMusic;

function SpotifyIcon() {
  return (
    <Layout.LayoutBase w={16} h={16}>
      <SvgIcon name="spotify" size={16} />
    </Layout.LayoutBase>
  );
}
